import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Copy, ExternalLink } from "lucide-react";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import { showInfo, showSuccess } from "shared/toast";
import { apiErrorMessage } from "../apiError";
import { KIND_LABELS } from "../outreachDates";
import { postTouchAction, type TouchView } from "../api/OutreachTouchesApi";
import { announceDueChange } from "../modal/useDueCounts";
import type { StoppedGroup } from "../dueStages";

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

const CAUSE_PILL = {
  replied: { tone: "success", label: "Replied" },
  skipped: { tone: "warning", label: "No LinkedIn profile" },
  late: { tone: "neutral", label: "Skipped" },
  other: { tone: "neutral", label: "Stopped" },
} as const;

function DroppedTouch({ touch }: { touch: TouchView }) {
  const [full, setFull] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(touch.text ?? ""); showSuccess("Copied"); }
    catch { setFull(true); showInfo("Could not copy automatically. The text is shown: select it and press Ctrl+C."); }
  }
  return (
    <div className="rounded-lg border border-border bg-surface-muted p-3" data-testid={`dropped-${touch.id}`}>
      <div className="flex items-center justify-between gap-2">
        <StatusPill tone="neutral" size="sm">{KIND_LABELS[touch.kind]} · {touch.state}</StatusPill>
        {touch.text && <Button variant="outline" size="sm" onClick={copy}><Copy className="w-3.5 h-3.5" /> Copy</Button>}
      </div>
      {touch.text && (
        <>
          {full
            ? <pre className="mt-2 whitespace-pre-wrap break-words text-sm text-foreground font-inter">{touch.text}</pre>
            : <p className="mt-2 truncate text-sm text-foreground font-inter">{touch.text}</p>}
          <button type="button" className="mt-1 text-xs text-muted-foreground underline underline-offset-2" aria-expanded={full} onClick={() => setFull((v) => !v)}>
            {full ? "Hide full text" : "Show full text"}
          </button>
        </>
      )}
    </div>
  );
}

/** One company's whole dropped LinkedIn path: the reason once, the dropped drafts listed under it, one Reopen for the path. */
export default function StoppedGroupCard({ group, onChanged }: { group: StoppedGroup; onChanged: (touchId: number) => void }) {
  const [open, setOpen] = useState(false);      // collapsed by default: the header says who and why, the drafts open on click
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const first = group.touches[0];
  // the contact and the reason come from the first touch that has them (a path can start at a touch with no link yet)
  const info = group.touches.find((t) => t.prime_contact_id) ?? first;
  const profileUrl = group.touches.find((t) => t.profile_url)?.profile_url ?? null;
  const reason = group.touches.map((t) => t.reason).find((r) => !!r) ?? null;
  const reopenTouch = group.touches.find((t) => t.actions.includes("reopen"));
  const pill = CAUSE_PILL[group.cause];

  async function reopen() {
    if (!reopenTouch) return;
    setBusy(true);
    setError("");
    try {
      const result = await postTouchAction(reopenTouch.opportunity_id, reopenTouch.id, { action: "reopen" });
      result.warnings.forEach((w) => showInfo(w));
      if (result.warnings.length === 0) showSuccess("Reopened");
      announceDueChange();
      onChanged(reopenTouch.id);
    } catch (err: any) {
      setError(apiErrorMessage(err, "Could not reopen. Reload and try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4 space-y-3 shadow-sm" data-testid={`stopped-${group.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
            {first.company_name ?? "Company"}
            <span className="font-normal text-muted-foreground">·</span>
            <Link className="font-normal text-muted-foreground underline underline-offset-2" to={`${crmBase()}?opportunity_id=${first.opportunity_id}`}>{first.rfp_title ?? "RFP"}</Link>
          </p>
          <p className="text-xs text-muted-foreground">
            {info.contact_name && <span className="text-sm text-foreground">{info.contact_name}</span>}
            {[info.contact_title, info.contact_email].filter(Boolean).map((v) => <span key={v}>{" · "}{v}</span>)}
            {info.prime_contact_id && (
              <>
                {" · "}
                <Link className="underline underline-offset-2" to={`${crmBase()}/prime-contacts/${info.prime_contact_id}`}>contact page</Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill tone={pill.tone} size="md">{pill.label}</StatusPill>
          <button type="button" aria-expanded={open} aria-label={open ? "Collapse details" : "Expand details"} onClick={() => setOpen((v) => !v)}
            className="rounded p-1 text-muted-foreground hover:bg-surface-muted">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {!open && reason && <p className="text-sm text-muted-foreground truncate">{reason}</p>}
      {open && (<>
      {profileUrl && (
        <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline underline-offset-2">
          <ExternalLink className="w-3 h-3" /> LinkedIn profile
        </a>
      )}
      {reason && <p className="text-sm text-foreground">{reason}</p>}
      {group.cause === "replied" && (
        <Link className="inline-block text-xs text-primary underline underline-offset-2" to={`${crmBase()}/due-today/conversations`}>View the conversation</Link>
      )}
      <p className="text-xs text-muted-foreground">
        Dropped: {group.touches.map((t) => KIND_LABELS[t.kind]).join(", ")}
      </p>
      <div className="space-y-2">{group.touches.map((t) => <DroppedTouch key={t.id} touch={t} />)}</div>
      {error && <Alert tone="danger" role="alert">{error}</Alert>}
      {reopenTouch && (
        <div className="pt-1">
          <Button variant="brand-outline" size="sm" disabled={busy} onClick={reopen}>Reopen the whole path</Button>
        </div>
      )}
      </>)}
    </div>
  );
}
